import { useLayoutEffect, useRef } from "react";
import type { ReactElement } from "react";
import { Link } from "react-router";

import styles from "./UsageMeter.module.css";

export interface UsageMeterProps {
  readonly delivered: number;
  /** `0` means the month has no generation limit: only the count is said. */
  readonly limit: number;
  readonly href: string;
  /** The sentence, from the dictionary; also the link's accessible name. */
  readonly text: string;
  /** The meter's own name, from the dictionary. */
  readonly meterLabel: string;
  /** In the 56px rail only the bar shows; the sentence stays the link's name. */
  readonly rail: boolean;
}

/** From this share of the limit on, the bar takes the warning colour (the mockup's `.meter.is-warn`). */
export const WARN_SHARE = 0.8;

/**
 * The sidebar's month meter (the mockup's `.meter-mini`): generations delivered against the
 * generation limit. It counts generations and never shows money (OD-12: money stays inside the
 * backoffice sections). The bar's width is a custom property written through the CSSOM, because the
 * CSP forbids `style=""`.
 */
export function UsageMeter({ delivered, limit, href, text, meterLabel, rail }: UsageMeterProps): ReactElement {
  const fillRef = useRef<HTMLElement>(null);
  const share = limit > 0 ? Math.min(delivered / limit, 1) : 0;

  useLayoutEffect(() => {
    fillRef.current?.style.setProperty("--meter-fill", `${String(Math.round(share * 100))}%`);
  }, [share]);

  return (
    <Link to={href} className={[styles.mini, rail ? styles.rail : undefined].filter(Boolean).join(" ")} aria-label={text}>
      <span className={styles.text} aria-hidden="true">
        {text}
      </span>
      {limit > 0 ? (
        <span
          className={[styles.meter, share >= WARN_SHARE ? styles.warn : undefined].filter(Boolean).join(" ")}
          role="meter"
          aria-label={meterLabel}
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-valuenow={delivered}
        >
          <i ref={fillRef} className={styles.fill} />
        </span>
      ) : null}
    </Link>
  );
}
